import * as is from 'is';

export class DashboardApplianceCache {
  #streamIdx = {}; // connection count by streamId
  #applianceIdx = {};
  #eventHandlers = {
    add: new Set(),
    remove: new Set(),
    update: new Set(),
  };
  constructor(options = {}) {
    const {
      autoAdd = false,
    } = options || {};

    this.autoAdd = autoAdd;
  }

  #handleEvents(event) {
    if (!Object.keys(this.#eventHandlers).includes(event)) {
      return;
    }
    for (const handler of this.#eventHandlers[event]) {
      if (!is.function(handler)) {
        continue;
      }
      handler(this.getData());
    }
  }

  /**
   * Adds the stream records to the cache.
   *
   * Add the stream information to the cache. If the stream's appliance is currently in the cache, the record will be
   * passed to the update() method.
   *
   * This method fires the 'add' event.
   *
   * @param  {...object} streamRecords The streams record data to add to the cache.
   * @returns {void}
   */
  add(...streamRecords) {
    for (const record of streamRecords) {
      const {
        id,
        connection_number: total = 0,
      } = record;

      if (id === undefined) {
        return;
      }

      const { applianceId, applianceName } = this.setApplianceData(record);

      if (applianceName === undefined) {
        return;
      }

      if (Object.keys(this.#streamIdx).includes(id)) {
        return this.update(record);
      }

      if (Object.keys(this.#applianceIdx).includes(applianceId)) {
        this.#applianceIdx[applianceId].streams += 1;
        this.#applianceIdx[applianceId].connectionCount += total;
        this.#streamIdx[id] = total;

        continue;
      }

      this.#streamIdx[id] = total;
      this.#applianceIdx[applianceId] = {
        id: applianceId,
        name: applianceName,
        streams: 1,
        connectionCount: total,
      };
    };

    this.#handleEvents('add');
  }

  /**
   * Updates a record's information.
   *
   * If the record isn't currently in the cache, the record is sent to the add() method.
   *
   * Updates the count of an existing stream.
   *
   * The method also fires off the 'update' handler event.
   *
   * @param {object} streamRecord The stream record to use for update.
   * @param {string} streamRecord.name  The stream record's name.
   * @param {object} streamRecord.appliance The stream record's appliance record.
   * @param {string | int} streamRecord.appliance.id  The appliance record's id.
   * @param {string} streamRecord.appliance.name  The appliance record's name.
   * @param {object} previousRecord  The stream's previously record.
   *
   * @returns {void}
   */
  update(streamRecord, previousRecord) {
    const {
      id,
      connection_number: total = 0,
    } = streamRecord;

    if (id === undefined) {
      return;
    }

    if (!Object.keys(this.#streamIdx).includes(id)) {
      if (this.autoAdd) {
        return this.add(streamRecord);
      }
      return;
    }

    const { applianceId } = this.setApplianceData(streamRecord);

    if (previousRecord && previousRecord.appliance_id !== applianceId) {
      this.updateAssociatedAppliance(previousRecord, applianceId);

      this.#handleEvents('update');
      return;
    }

    const delta = total - this.#streamIdx[id];
    this.#streamIdx[id] += delta;
    this.#applianceIdx[applianceId].connectionCount += delta;
    this.#handleEvents('update');
  }

  /**
   * Moves stream/connection counts from one appliance to another.
   *
   * If an appliance no longer has streams, it will be deleted from the cache.
   *
   * @param {object} previousRecord The previous stream record from the payload.
   * @param {string} newApplianceId  The id of the appliance to move the counts to.
   * @returns {void}
   */
  updateAssociatedAppliance(previousRecord, newApplianceId) {
    const {
      connection_number: prevTotal = 0,
      appliance_id,
    } = previousRecord;

    this.#applianceIdx[appliance_id].connectionCount -= prevTotal;
    this.#applianceIdx[appliance_id].streams -= 1;

    if (this.#applianceIdx[appliance_id].streams === 0) {
      delete this.#applianceIdx[appliance_id];
    }

    this.#applianceIdx[newApplianceId].streams += 1;
    this.#applianceIdx[newApplianceId].connectionCount += prevTotal;
  }

  /**
   * Removes a record from the cache.
   *
   * If the record does not exist in the cache, the method exits gracefully.
   *
   * The method removes the current count of the existing stream record before removing the record's entry from the
   * cache.
   *
   * @param {object} streamRecord The stream record to use for update.
   * @param {string} streamRecord.name  The stream record's name.
   * @param {object} streamRecord.appliance The stream record's appliance record.
   * @param {string | int} streamRecord.appliance.id  The appliance record's id.
   * @param {string} streamRecord.appliance.name  The appliance record's name.
   * @returns {void}
   */
  remove(streamRecord) {
    const {
      id,
    } = streamRecord;

    if (id === undefined) {
      return;
    }

    if (!Object.keys(this.#streamIdx).includes(id)) {
      return;
    }

    // For a newly created stream, the appliance object is not availble on the mesasge
    const { applianceId } = this.setApplianceData(streamRecord);

    this.#applianceIdx[applianceId].connectionCount -= this.#streamIdx[id];
    this.#applianceIdx[applianceId].streams -= 1;
    delete this.#streamIdx[id];

    if (!this.#applianceIdx[applianceId].streams) {
      delete this.#applianceIdx[applianceId];
    }

    this.#handleEvents('remove');
  }

  /**
   * Adds a new appliance to the cache using the initial API data
   * @param {*} id of the appliance
   * @param {*} name of the appliance
   * @param {*} streams default to 0
   * @param {*} connectionCount default to 0
   */
  addAppliance(id, name, streams = 0, connectionCount = 0) {
    this.#applianceIdx[id] = {
      id,
      name,
      streams,
      connectionCount,
    };
  }

  /**
   * Updates the name of an appliance within the cache.
   *
   * @param {object} applianceRecord The appliance record to use for update.
   * @param {string} applianceRecord.id  The applianceRecord record's id.
   * @param {string} applianceRecord.name  The applianceRecord record's name.
   * @returns {void}
   */
  updateApplianceName(applianceRecord) {
    const {
      id,
      name,
    } = applianceRecord;

    if (!Object.keys(this.#applianceIdx).includes(id)) {
      return;
    }

    this.#applianceIdx[id] = {
      ...this.#applianceIdx[id],
      name: name,
    };

    this.#handleEvents('update');
  }

  /**
 * Removes a record from the cache.
 *
 *
 * @param {string} id The id for the appliance to remove.
 * @returns {void}
 */
  removeAppliance(appliance) {
    const { id } = appliance;
    if (this.#applianceIdx[id]) {
      this.#applianceIdx[id].streams = 0;
      this.#applianceIdx[id].connectionCount = 0;
      delete this.#applianceIdx[id];
      this.#handleEvents('remove');
    }
  }

  updateApplianceConnectionCount(appliance, { silent = false } = {}) {
    const { id, total_connection_count } = appliance;
    if (this.#applianceIdx[id]) {
      this.#applianceIdx[id].connectionCount = total_connection_count;
      if (!silent) this.#handleEvents('update');
    }
  }

  isApplianceInCache(id) {
    return Boolean(this.#applianceIdx[id]);
  }

  getData() {
    const response = [];
    for (const entry of Object.values(this.#applianceIdx)) {
      if (!entry.streams) {
        continue;
      }
      response.push(
        {
          id: entry.id,
          name: entry.name,
          type: 'stream count',
          count: entry.streams,
        },
        {
          id: entry.id,
          name: entry.name,
          type: 'connection count',
          count: entry.connectionCount,
        }
      );
    }
    return response;
  }
  addEventHandler(handler, ...events) {
    if (!is.function(handler)) {
      return;
    }
    if (events.includes('*')) {
      for (const eventName of Object.keys(this.#eventHandlers)) {
        this.#eventHandlers[eventName].add(handler);
      }
      return;
    }
    for (const event of events) {
      if (Object.keys(this.#eventHandlers).includes(event)) {
        this.#eventHandlers[event].add(handler);
      }
    }
  }

  /**
 * Extracts the appliance name and id from the record
 *
 * @param {object} streamRecord The stream record or stream payload to extract information from.
 * @returns {object}
 */
  setApplianceData(record) {
    let applianceId;
    let applianceName;
    if (!record.appliance) {
      applianceId = record.appliance_id;
      applianceName = this.#applianceIdx[applianceId]?.name || undefined;
    } else {
      applianceId = record.appliance.id;
      applianceName = record.appliance.name;
    }

    return {
      applianceId,
      applianceName,
    };
  }

  // Private object getters for unit testing purposes
  getStreamIdx() {
    return this.#streamIdx;
  }

  getApplianceIdx() {
    return this.#applianceIdx;
  }
  /////////
}
